import { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, MenuItem,
  Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { ImportPartsDialog } from './ImportPartsDialog'
import SearchIcon from '@mui/icons-material/Search'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import LinkIcon from '@mui/icons-material/Link'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { motion } from 'framer-motion'
import { Virtuoso } from 'react-virtuoso'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { AssetCategory, CreatePartRequest, EmailSignature, Part, PartStatus, Vendor } from '@cmms/types'

import { primary } from '../../theme/palette'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useCopyLink } from '../../hooks/useCopyLink'
import { useConfirm } from '../../components/ConfirmDialog'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { AttachmentSection } from '../../components/AttachmentSection'
import { useAuthStore } from '../../store/authStore'
import { getCategoryIcon } from '../asset-categories/iconRegistry'
import { SearchableSelect } from '../../components/SearchableSelect'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { CustomerScopeChip } from '../../components/CustomerScopeChip'
import { DateRangeFilterPills, useDateRangeFilter, WorkOrderTimelineList, type WorkOrderTimelineItem } from '../../components/WorkOrderTimeline'
import { BulkActionBar } from '../../components/BulkActionBar'
import { useBulkSelection } from '../../hooks/useBulkSelection'

const STATUS_META: Record<string, { label: string; color: string }> = {
  in_stock:     { label: 'In stock',     color: '#059669' },
  low_stock:    { label: 'Low stock',    color: '#D97706' },
  out_of_stock: { label: 'Out of stock', color: '#DC2626' },
}

function totalStockValue(parts: Part[]): number {
  return parts.reduce((sum, p) => sum + (Number(p.unit_cost) || 0) * p.quantity, 0)
}

export function InventoryPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { partId: urlPartId } = useParams<{ partId?: string }>()
  const { formatCurrency } = useOrgSettings()
  const { can } = usePermissions()

  const [selectedId, setSelectedId] = useState<string | null>(urlPartId ?? null)
  useEffect(() => { if (urlPartId) setSelectedId(urlPartId) }, [urlPartId])
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [formOpen, setFormOpen] = useState<{ mode: 'create' } | { mode: 'edit'; part: Part } | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  const categoriesQ = useQuery({ queryKey: ['asset-categories'], queryFn: () => api.assets.listCategories(), staleTime: 5 * 60 * 1000 })
  const categories: AssetCategory[] = Array.isArray(categoriesQ.data) ? categoriesQ.data : []

  const partsQ = useQuery({
    queryKey: ['parts', { category: catFilter !== 'all' ? catFilter : undefined, status: statusFilter !== 'all' ? statusFilter : undefined, q: search.trim() || undefined }],
    queryFn: () => api.parts.list({
      category: catFilter !== 'all' ? catFilter : undefined,
      status: statusFilter !== 'all' ? statusFilter as PartStatus : undefined,
      q: search.trim() || undefined,
    }),
  })

  const parts: Part[] = Array.isArray(partsQ.data) ? partsQ.data : []

  if (!selectedId && parts.length > 0) {
    const first = parts[0]!.id
    setSelectedId(first)
    navigate(`/inventory/${first}`, { replace: true })
  }
  function handleSelect(id: string) {
    setSelectedId(id)
    navigate(`/inventory/${id}`, { replace: true })
  }

  const selected = selectedId ? parts.find((p) => p.id === selectedId) ?? null : null
  const lowCount = parts.filter((p) => p.status === 'low_stock').length
  const outCount = parts.filter((p) => p.status === 'out_of_stock').length
  const activeFilters = (catFilter !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0)

  const confirmDlg = useConfirm()
  const bulk = useBulkSelection()
  const bulkDeleteM = useMutation({
    mutationFn: (ids: string[]) => api.parts.bulkRemove(ids),
    onSuccess: (res) => {
      const okCount = res.deleted.length
      const skipCount = res.skipped.length
      if (okCount > 0 && skipCount === 0) toast.success(`Deleted ${okCount} part${okCount === 1 ? '' : 's'}`)
      else if (okCount > 0 && skipCount > 0) toast.success(`Deleted ${okCount}, skipped ${skipCount}`)
      else toast.error('Nothing was deleted')
      qc.invalidateQueries({ queryKey: ['parts'] })
      bulk.clear()
      if (selectedId && res.deleted.includes(selectedId)) setSelectedId(null)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Bulk delete failed'),
  })
  async function handleBulkDelete() {
    const ids = bulk.ids
    if (ids.length === 0) return
    const ok = await confirmDlg({
      title: `Delete ${ids.length} part${ids.length === 1 ? '' : 's'}?`,
      message: 'They will be soft-deleted and disappear from this list. You can re-import them anytime.',
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) bulkDeleteM.mutate(ids)
  }

  const toolbar = (
    <Stack spacing={1.5}>
      {(lowCount + outCount > 0) && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Stack direction="row" alignItems="center" spacing={1}
            sx={{
              px: 2, py: 1, borderRadius: 1.5,
              bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(217,119,6,0.06)' : 'rgba(217,119,6,0.16)',
              border: '1px solid',
              borderColor: (t) => t.palette.mode === 'light' ? 'rgba(217,119,6,0.25)' : 'rgba(217,119,6,0.40)',
            }}>
            <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: '#D97706' }} />
            <Typography sx={{ fontSize: 12.5, flex: 1 }}>
              <strong>{lowCount}</strong> low stock · <strong>{outCount}</strong> out of stock
            </Typography>
            <Button size="small" onClick={() => setStatusFilter('low_stock')}
              sx={{ fontSize: 12, fontWeight: 600, color: '#D97706' }}>Review</Button>
          </Stack>
        </motion.div>
      )}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <TextField placeholder="Search parts" value={search} onChange={(e) => setSearch(e.target.value)} size="small"
          sx={{ width: { xs: '100%', md: 300 }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 13, borderRadius: 2, '& fieldset': { borderColor: 'divider' } } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }} />
        <Box sx={{ width: 1 }} />
        <FilterChip icon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />}
          label={catFilter === 'all' ? 'Category' : (categories.find((c) => c.name === catFilter)?.name ?? catFilter)}
          active={catFilter !== 'all'}
          value={catFilter} onChange={setCatFilter}
          options={[{ value: 'all', label: 'All categories' }, ...categories.map((c) => ({ value: c.name, label: c.name }))]} />
        <FilterChip icon={<WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />}
          label={statusFilter === 'all' ? 'Stock status' : STATUS_META[statusFilter]?.label ?? 'Status'}
          active={statusFilter !== 'all'} value={statusFilter}
          onChange={setStatusFilter}
          options={[{ value: 'all', label: 'All' }, { value: 'in_stock', label: 'In stock' }, { value: 'low_stock', label: 'Low stock' }, { value: 'out_of_stock', label: 'Out of stock' }]} />
        {activeFilters > 0 && (
          <Button size="small" onClick={() => { setCatFilter('all'); setStatusFilter('all') }}
            startIcon={<TuneOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: 12, color: 'text.secondary' }}>Clear ({activeFilters})</Button>
        )}
      </Stack>
    </Stack>
  )

  return (
    <>
      <PageShell title="Inventory" toolbar={toolbar} scrollContent={false}
        actions={
          <Stack direction="row" spacing={1}>
            <Tooltip title={can('inventory:create') ? '' : "You don't have permission to add parts"} arrow>
              <span>
                <Button variant="outlined" startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setImportOpen(true)} disabled={!can('inventory:create')}
                  sx={{ fontSize: 13, color: 'text.secondary', borderColor: 'divider' }}>
                  Import</Button>
              </span>
            </Tooltip>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
              <Tooltip title={can('inventory:create') ? '' : "You don't have permission to add parts"} arrow>
                <span>
                  <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setFormOpen({ mode: 'create' })} disabled={!can('inventory:create')}
                    sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, boxShadow: primary.glow }}>
                    New part</Button>
                </span>
              </Tooltip>
            </motion.div>
          </Stack>
        }>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: { xs: 3, md: 4 } }}>
          {partsQ.isLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
          ) : (
            <>
              {/* List */}
              <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, width: { xs: '100%', md: 360 }, flexShrink: 0, borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                  {can('inventory:delete') && parts.length > 0 && (
                    <Checkbox size="small"
                      checked={bulk.allSelected(parts.map((p) => p.id))}
                      indeterminate={bulk.someSelected(parts.map((p) => p.id))}
                      onChange={(e) => bulk.replaceWith(e.target.checked ? parts.map((p) => p.id) : [])}
                      sx={{ p: 0.5 }} />
                  )}
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
                    {bulk.count > 0
                      ? `${bulk.count} selected`
                      : `${parts.length} part${parts.length === 1 ? '' : 's'} · ${formatCurrency(totalStockValue(parts))} value`}
                  </Typography>
                </Stack>
                {parts.length === 0 && !partsQ.isLoading ? (
                  <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No parts yet.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    {/* Virtualized via react-virtuoso (same pattern as
                        AssetList/RequestsPage) — with hundreds of parts,
                        mounting every row plus its inline category
                        lookup made the list slow to reconcile. */}
                    <Virtuoso
                      style={{ height: '100%' }}
                      data={parts}
                      increaseViewportBy={200}
                      itemContent={(_idx, p) => {
                        const sel = p.id === selectedId
                        const st = STATUS_META[p.status] ?? { label: 'In stock', color: '#059669' }
                        return (
                          <Stack direction="row" alignItems="center" spacing={1.5} onClick={() => handleSelect(p.id)}
                            sx={{
                              position: 'relative', px: 1.5, py: 1.25, cursor: 'pointer',
                              transition: 'background-color 120ms ease',
                              bgcolor: sel ? primary.selected : 'transparent',
                              '&:hover': { bgcolor: sel ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
                              '&::before': sel ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
                            }}>
                            {can('inventory:delete') && (
                              <Checkbox size="small" checked={bulk.isSelected(p.id)}
                                onClick={(e) => { e.stopPropagation(); bulk.toggle(p.id) }}
                                onChange={() => {}}
                                sx={{ p: 0.5 }} />
                            )}
                            <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.05)', color: 'text.secondary', flexShrink: 0, position: 'relative' }}>
                              <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />
                              <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 10, height: 10, borderRadius: 99, bgcolor: st.color, border: '2px solid', borderColor: 'background.paper' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 13.5, fontWeight: sel ? 600 : 500 }} noWrap>{p.name}</Typography>
                              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.2 }}>
                                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{p.sku ?? p.part_code}</Typography>
                                {p.category && (() => {
                                  const catColor = categories.find((c) => c.name === p.category)?.color
                                  return <>
                                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>·</Typography>
                                    {catColor && <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: catColor, flexShrink: 0 }} />}
                                    <Typography sx={{ fontSize: 11, color: catColor ?? 'text.secondary' }}>{p.category}</Typography>
                                  </>
                                })()}
                                <CustomerScopeChip customerId={p.customer_id} />
                              </Stack>
                            </Box>
                            <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{p.quantity}</Typography>
                              <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>qty</Typography>
                            </Stack>
                          </Stack>
                        )
                      }}
                    />
                  </Box>
                )}
                <BulkActionBar count={bulk.count} label="part"
                  deleting={bulkDeleteM.isPending}
                  onClear={bulk.clear} onDelete={handleBulkDelete} />
              </Box>

              {/* Detail */}
              {selected ? <PartDetail part={selected} onEdit={() => setFormOpen({ mode: 'edit', part: selected })} categories={categories} /> : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select a part</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </PageShell>

      {formOpen && <PartFormDialog mode={formOpen.mode} part={formOpen.mode === 'edit' ? formOpen.part : undefined} categories={categories} onClose={() => setFormOpen(null)} />}
      <ImportPartsDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </>
  )
}

function PartDetail({ part, onEdit, categories }: { part: Part; onEdit: () => void; categories: AssetCategory[] }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const copyLink = useCopyLink()
  const confirmDlg = useConfirm()
  const { formatDate, formatCurrency, prefs } = useOrgSettings()
  const { can } = usePermissions()
  const st = STATUS_META[part.status] ?? { label: 'In stock', color: '#059669' }
  const [reorderOpen, setReorderOpen] = useState(false)

  const deleteM = useMutation({
    mutationFn: () => api.parts.remove(part.id),
    onSuccess: () => { toast.success('Part deleted'); qc.invalidateQueries({ queryKey: ['parts'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <motion.div key={part.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26 }}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{ width: 48, height: 48, borderRadius: 1.75, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.06)', color: 'text.secondary', flexShrink: 0 }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{part.name}</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>{part.sku ?? part.part_code}</Typography>
              <Chip label={st.label} size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: `${st.color}18`, color: st.color, '& .MuiChip-label': { px: 1 } }} />
              {part.category && (() => {
                const cat = categories.find((c) => c.name === part.category)
                const catColor = cat?.color ?? '#64748B'
                return <Chip label={part.category} size="small"
                  sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: `${catColor}18`, color: catColor, '& .MuiChip-label': { px: 1 } }} />
              })()}
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Tooltip title="Copy link" arrow>
              <IconButton size="small" onClick={() => copyLink(`/inventory/${part.id}`)}
                sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34 }}>
                <LinkIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            <Tooltip title={can('inventory:update') ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={onEdit} disabled={!can('inventory:update')}
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider', height: 34 }}>Edit</Button></Box>
            </Tooltip>
            <Tooltip title={can('inventory:delete') ? 'Delete' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><IconButton size="small" disabled={!can('inventory:delete')} onClick={async () => {
                const ok = await confirmDlg({ title: `Delete ${part.name}?`, message: 'This part will be permanently removed.', confirmLabel: 'Delete', variant: 'danger' })
                if (ok) deleteM.mutate()
              }} sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34, '&:hover': { color: 'error.main' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Box></Tooltip>
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3, scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
        {part.description && (
          <><Sec title="Description"><Typography sx={{ fontSize: 13.5, lineHeight: 1.65 }}>{part.description}</Typography></Sec><Divider /></>
        )}
        <Sec title="Stock levels">
          <Stack spacing={1.25}>
            <Kv icon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />} label="In stock" value={`${part.quantity} units`}
              tip="Current number of this part available in your inventory" />
            <Kv icon={<WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />} label="Min. stock" value={`${part.min_quantity} units`}
              tip="When stock drops to this level, the part will be flagged as 'Low stock' so you can reorder in time" />
            <Kv icon={<Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1, width: 15, textAlign: 'center' }}>{prefs.currencySymbol}</Typography>} label="Price per unit" value={part.unit_cost != null ? formatCurrency(Number(part.unit_cost)) : '—'}
              tip="How much one unit of this part costs" />
            <Kv icon={<Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1, width: 15, textAlign: 'center' }}>{prefs.currencySymbol}</Typography>} label="Total value" value={formatCurrency(Number(part.unit_cost ?? 0) * part.quantity)}
              tip="Price per unit multiplied by current stock quantity" />
          </Stack>
        </Sec>
        <Divider />
        <Sec title="Where it's stored">
          <Stack spacing={1.25}>
            {part.location_name && (
              <Kv icon={<LocationOnOutlinedIcon sx={{ fontSize: 15 }} />} label="Location" value={part.location_name}
                tip="Where this part is physically stored"
                onClick={part.location_id ? () => navigate(`/locations/${part.location_id}`) : undefined} />
            )}
            {part.vendor_name && (
              <Kv icon={<LocalShippingOutlinedIcon sx={{ fontSize: 15 }} />} label="Supplier" value={part.vendor_name}
                tip="The vendor or supplier you order this part from"
                onClick={part.vendor_id ? () => navigate(`/vendors/${part.vendor_id}`) : undefined} />
            )}
            {part.last_restocked && <Kv icon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />} label="Last restocked" value={formatDate(part.last_restocked)}
              tip="When this part was last received or restocked" />}
          </Stack>
        </Sec>
        <Divider />
        <Sec title="Info">
          <Stack spacing={1.25}>
            <Kv icon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />} label="Part code" value={part.part_code}
              tip="Auto-generated unique identifier for this part" />
            <Kv icon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />} label="Added on" value={formatDate(part.created_at)}
              tip="When this part was first added to the system" />
          </Stack>
        </Sec>
        {part.notes && (
          <><Divider /><Sec title="Notes"><Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: 'text.secondary' }}>{part.notes}</Typography></Sec></>
        )}

        <Divider />

        {/* Attachments */}
        <Box sx={{ py: 2.5 }}>
          <AttachmentSection
            queryKey={['part-attachments', part.id]}
            listFn={() => api.parts.listAttachments(part.id)}
            uploadFn={(file, onProgress) => api.parts.uploadAttachment(part.id, file, onProgress)}
            viewFn={(doc) => api.parts.viewAttachment(part.id, doc.id)}
            deleteFn={(docId) => api.parts.deleteAttachment(part.id, docId)}
            canEdit={can('inventory:update')}
            maxFiles={5}
            title="Photos & documents"
          />
        </Box>

        <Divider />
        <PartHistory partId={part.id} />
      </Box>
      <Box sx={{ flexShrink: 0, px: 4, py: 2.25, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
        <Tooltip title={!can('inventory:reorder') ? "You don't have permission to reorder" : part.vendor_name ? `Send a reorder email to ${part.vendor_name}` : 'Send a reorder request via email'} arrow>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Button variant="outlined" startIcon={<LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setReorderOpen(true)} disabled={!can('inventory:reorder')}
              sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', borderColor: 'primary.main', borderRadius: 99, px: 2.5, py: 0.75 }}>
              Reorder from vendor</Button>
          </Box>
        </Tooltip>
      </Box>

      {reorderOpen && <ReorderDialog part={part} onClose={() => setReorderOpen(false)} />}
    </motion.div>
  )
}

function ReorderDialog({ part, onClose }: { part: Part; onClose: () => void }) {
  const { user } = useAuthStore()
  const { org } = useOrgSettings()
  const navigate = useNavigate()
  const prefs = (user?.preferences ?? {}) as Record<string, any>
  const signatures: EmailSignature[] = Array.isArray(prefs.signatures) ? prefs.signatures : []
  const defaultSig = signatures.find((s) => s.isDefault) ?? signatures[0]

  const vendorQ = useQuery({
    queryKey: ['vendor', part.vendor_id],
    queryFn: () => api.vendors.get(part.vendor_id!),
    enabled: !!part.vendor_id,
  })
  const vendor: Vendor | null = vendorQ.data ?? null

  const suggestedQty = Math.max((part.min_quantity * 2) - part.quantity, part.min_quantity)

  const [to, setTo] = useState('')
  const [subject, setSubject] = useState(`Reorder request: ${part.name} (${part.sku ?? part.part_code})`)
  const [body, setBody] = useState('')
  const [sigId, setSigId] = useState(defaultSig?.id ?? '')

  useEffect(() => {
    if (vendor?.email && !to) setTo(vendor.email)
  }, [vendor])

  useEffect(() => {
    const contactName = vendor?.contact_name ?? vendor?.name ?? 'there'
    setBody(
`Hi ${contactName},

We'd like to place a reorder for the following part:

Part: ${part.name}
SKU: ${part.sku ?? part.part_code}
Current stock: ${part.quantity} units
Suggested order: ${suggestedQty} units

Please confirm availability and lead time.

Thanks,
${user?.name ?? ''}`
    )
  }, [vendor, part])

  const selectedSig = signatures.find((s) => s.id === sigId)

  const sendM = useMutation({
    mutationFn: () => api.parts.sendReorderEmail(part.id, {
      to, subject, message: body,
      signature: selectedSig?.body,
      org_name: org?.name,
      sender_name: user?.name,
    }),
    onSuccess: () => { toast.success('Reorder email sent!'); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to send email'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Reorder from vendor</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Summary card */}
          <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Part</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{part.name}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>SKU</Typography>
                <Typography sx={{ fontSize: 12.5, fontFamily: 'ui-monospace, monospace' }}>{part.sku ?? part.part_code}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Current stock</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: part.quantity === 0 ? '#DC2626' : part.quantity <= part.min_quantity ? '#D97706' : 'text.primary' }}>
                  {part.quantity} units
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Suggested order</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.main' }}>{suggestedQty} units</Typography>
              </Stack>
            </Stack>
          </Box>

          <Tooltip title="The vendor's email address — this is where the reorder request will be sent" arrow placement="top-start">
            <TextField label="Send to (email)" value={to} onChange={(e) => setTo(e.target.value)} size="small" fullWidth
              placeholder="vendor@example.com" type="email"
              helperText={!vendor?.email && part.vendor_name ? `No email on file for ${part.vendor_name} — enter one manually` : undefined} />
          </Tooltip>

          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} size="small" fullWidth />

          <TextField label="Message" value={body} onChange={(e) => setBody(e.target.value)} size="small" fullWidth multiline minRows={6}
            helperText="You can edit the message before sending" />

          {/* Signature selector */}
          <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
              Signature
            </Typography>
            {signatures.length > 0 ? (
              <>
                <SearchableSelect
                  value={sigId}
                  onChange={setSigId}
                  options={signatures}
                  getValue={(s) => s.id}
                  getLabel={(s) => `${s.name}${s.isDefault ? ' (default)' : ''}`}
                  placeholder="No signature"
                />
                {selectedSig && (
                  <Box sx={{ mt: 1.5, p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Preview</Typography>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{selectedSig.body}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No signatures created yet.</Typography>
                <Button size="small" onClick={() => { onClose(); navigate('/settings') }}
                  sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>Create one in Settings</Button>
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!to.trim() || !subject.trim() || !body.trim() || sendM.isPending}
          onClick={() => sendM.mutate()}
          startIcon={<LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />}>
          {sendM.isPending ? 'Sending...' : 'Send reorder email'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PartFormDialog({ mode, part, categories, onClose }: { mode: 'create' | 'edit'; part?: Part; categories: AssetCategory[]; onClose: () => void }) {
  const qc = useQueryClient()
  const { prefs } = useOrgSettings()

  const vendorsQ = useQuery({ queryKey: ['vendors'], queryFn: () => api.vendors.list(), staleTime: 60_000 })
  const vendors: Vendor[] = Array.isArray(vendorsQ.data) ? vendorsQ.data : []

  const [name, setName] = useState(part?.name ?? '')
  const [description, setDescription] = useState(part?.description ?? '')
  const [sku, setSku] = useState(part?.sku ?? '')
  const [category, setCategory] = useState(part?.category ?? '')
  const [quantity, setQuantity] = useState(part?.quantity != null ? String(part.quantity) : '0')
  const [minQuantity, setMinQuantity] = useState(part?.min_quantity != null ? String(part.min_quantity) : '0')
  const [unitCost, setUnitCost] = useState(part?.unit_cost != null ? String(part.unit_cost) : '')
  const [vendorId, setVendorId] = useState(part?.vendor_id ?? '')
  const [siteId, setSiteId] = useState(part?.site_id ?? '')
  const [notes, setNotes] = useState(part?.notes ?? '')
  // Catalog scope: null = shared (visible to every customer).
  const [customerId, setCustomerId] = useState<string | null>((part as any)?.customer_id ?? null)

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const sites = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const m = useMutation({
    mutationFn: () => {
      // On edit, a field the user just cleared must be sent as an
      // explicit `null` — omitting it (`undefined`) means "leave
      // unchanged" to the backend's sparse update, so the old value
      // would silently survive a "successful" save.
      const empty = mode === 'edit' ? null : undefined
      const body = {
        name: name.trim(),
        description: description.trim() || empty,
        sku: sku.trim() || empty,
        category: category || empty,
        quantity: parseInt(quantity) || 0,
        min_quantity: parseInt(minQuantity) || 0,
        unit_cost: unitCost ? parseFloat(unitCost) : empty,
        vendor_id: vendorId || empty,
        site_id: siteId || null,
        notes: notes.trim() || empty,
        // Always send customer_id — `null` means "shared org-wide"
        // and must be settable from the form (not omitted).
        customer_id: customerId,
      }
      // `empty` is always `undefined` when mode === 'create', so `body`
      // genuinely satisfies CreatePartRequest at runtime in that branch.
      return mode === 'create' ? api.parts.create(body as unknown as CreatePartRequest) : api.parts.update(part!.id, body)
    },
    onSuccess: () => { toast.success(mode === 'create' ? 'Part created' : 'Part updated'); qc.invalidateQueries({ queryKey: ['parts'] }); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{mode === 'create' ? 'New part' : `Edit ${part?.name}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Part name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus
            placeholder="e.g. HVAC Air Filter 20x25x1" helperText="Give the part a clear, recognizable name" />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" fullWidth multiline minRows={2}
            placeholder="What is this part used for?" />
          <Stack direction="row" spacing={2}>
            <Tooltip title="A unique code used to identify this part (e.g. from the manufacturer or your own system)" arrow placement="top">
              <TextField label="SKU / Part number" value={sku} onChange={(e) => setSku(e.target.value)} size="small" fullWidth placeholder="e.g. FLT-2025-01" />
            </Tooltip>
            <SearchableSelect
              value={category}
              onChange={setCategory}
              options={categories}
              getValue={(c) => c.name}
              getLabel={(c) => c.name}
              placeholder="Select category"
              renderOption={(c) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {c.color && <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: c.color, flexShrink: 0 }} />}
                  <Box component="span">{c.name}</Box>
                </Stack>
              )}
            />
          </Stack>
          <Divider />
          <Stack direction="row" spacing={2}>
            <Tooltip title="How many units you currently have in stock" arrow placement="top">
              <TextField label="Current stock" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} size="small" fullWidth />
            </Tooltip>
            <Tooltip title="You'll get a 'Low stock' alert when stock drops to this number — set it high enough to allow time for reordering" arrow placement="top">
              <TextField label="Minimum stock level" type="number" value={minQuantity} onChange={(e) => setMinQuantity(e.target.value)} size="small" fullWidth />
            </Tooltip>
          </Stack>
          <Tooltip title="The cost of one unit — used to calculate total inventory value" arrow placement="top">
            <TextField label="Price per unit" type="number" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} size="small" fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">{prefs.currencySymbol}</InputAdornment> }} />
          </Tooltip>
          <Divider />
          <Tooltip title="The supplier you order this part from — helps track where to reorder" arrow placement="top">
            <Box>
              <SearchableSelect
                value={vendorId}
                onChange={setVendorId}
                options={vendors}
                getValue={(v) => v.id}
                getLabel={(v) => v.name}
                placeholder="Select supplier / vendor"
              />
            </Box>
          </Tooltip>
          <CustomerScopePicker
            mode="catalog"
            value={customerId}
            onChange={setCustomerId}
            helperText="All = visible to every customer's inventory. Pick one to scope this part."
          />
          <Tooltip title="Restrict this part to a specific site, or leave as 'All sites' for everyone" arrow placement="top-start">
            <Box>
              <SearchableSelect
                value={siteId}
                onChange={setSiteId}
                options={sites}
                getValue={(s: any) => s.id}
                getLabel={(s: any) => s.name}
                placeholder="All sites (no restriction)"
              />
            </Box>
          </Tooltip>
          <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} size="small" fullWidth multiline minRows={2}
            placeholder="Any extra details — storage instructions, compatible equipment, etc." />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create part' : 'Save changes')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PartHistory({ partId }: { partId: string }) {
  const { formatDate } = useOrgSettings()
  const navigate = useNavigate()
  const [subTab, setSubTab] = useState<'activity' | 'work_orders'>('activity')

  const auditQ = useQuery({
    queryKey: ['part-audit', partId],
    queryFn: () => api.parts.getAuditLog(partId),
    enabled: subTab === 'activity',
  })
  const log = Array.isArray(auditQ.data) ? auditQ.data : []

  const ACTION_ICONS: Record<string, { color: string; label: string }> = {
    created:              { color: '#3B82F6', label: 'Added to inventory' },
    restocked:            { color: '#059669', label: 'Restocked' },
    adjusted:             { color: '#D97706', label: 'Stock adjusted' },
    used_in_work_order:   { color: '#DC2626', label: 'Used in work order' },
    reorder_sent:         { color: '#8B5CF6', label: 'Reorder sent' },
  }

  return (
    <Box sx={{ py: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>History</Typography>
        <ToggleButtonGroup size="small" value={subTab} exclusive onChange={(_, v) => v && setSubTab(v)}>
          <ToggleButton value="activity" sx={{ height: 28, px: 1.5, fontSize: 12, fontWeight: 600, textTransform: 'none' }}>
            Activity Log
          </ToggleButton>
          <ToggleButton value="work_orders" sx={{ height: 28, px: 1.5, fontSize: 12, fontWeight: 600, textTransform: 'none' }}>
            Work Orders
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {subTab === 'activity' ? (
        <>
          {auditQ.isLoading && <CircularProgress size={18} />}
          {!auditQ.isLoading && log.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic' }}>No history yet</Typography>
          )}
          <Stack spacing={0}>
            {log.map((entry: any, i: number) => {
              const meta = ACTION_ICONS[entry.action] ?? { color: '#64748B', label: entry.action }
              return (
                <Box key={entry.id} sx={{ borderBottom: i < log.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: meta.color, mt: 0.75, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{meta.label}</Typography>
                      {entry.details && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>{entry.details}</Typography>}
                      {entry.work_order_title && (
                        <Typography onClick={() => navigate('/work-orders')}
                          sx={{ fontSize: 11.5, color: 'primary.main', mt: 0.35, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                          WO: {entry.work_order_title}
                        </Typography>
                      )}
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        {entry.user_name && <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>by {entry.user_name}</Typography>}
                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{formatDate(entry.created_at, true)}</Typography>
                      </Stack>
                    </Box>
                    {entry.quantity_change != null && (
                      <Typography sx={{ fontSize: 13, fontWeight: 700, flexShrink: 0,
                        color: entry.quantity_change > 0 ? '#059669' : entry.quantity_change < 0 ? '#DC2626' : 'text.secondary' }}>
                        {entry.quantity_change > 0 ? '+' : ''}{entry.quantity_change}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        </>
      ) : (
        <PartWorkOrdersPanel partId={partId} />
      )}
    </Box>
  )
}

function PartWorkOrdersPanel({ partId }: { partId: string }) {
  const { formatDate } = useOrgSettings()
  const navigate = useNavigate()
  const woQ = useQuery({
    queryKey: ['part-work-orders', partId],
    queryFn: () => api.parts.listWorkOrders(partId),
  })
  const rows = Array.isArray(woQ.data) ? woQ.data : []
  const sorted = [...rows].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  const { range, setRange, filtered } = useDateRangeFilter(sorted, (r) => r.created_at)

  const items: WorkOrderTimelineItem[] = filtered.map((r) => ({
    id: r.work_order_id,
    title: r.title,
    code: r.wo_code,
    status: r.status,
    priority: r.priority,
    assignee: r.assigned_to_user_name ?? r.assigned_to_team_name ?? null,
    createdAt: r.created_at,
    finishedAt: r.closed_at ?? r.resolved_at ?? null,
    extraChip: {
      label: `${r.deducted ? 'Used' : 'Reserved'}: ${r.quantity}`,
      color: r.deducted ? '#DC2626' : '#D97706',
      bg: r.deducted ? '#FEF2F2' : '#FFFBEB',
    },
    onClick: () => navigate(`/work-orders/${r.work_order_id}`),
  }))

  return (
    <Box sx={{ mt: 0.5 }}>
      <DateRangeFilterPills range={range} onChange={setRange} />
      <WorkOrderTimelineList
        items={items}
        formatDate={formatDate}
        loading={woQ.isLoading}
        emptyMessage={sorted.length === 0 ? 'This part hasn’t been used on any work order yet.' : 'No usage in this date range.'}
      />
    </Box>
  )
}

function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return <Box sx={{ py: 2.5 }}><Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.25 }}>{title}</Typography>{children}</Box>
}
function Kv({ icon, label, value, tip, onClick }: { icon: React.ReactNode; label: string; value: string; tip?: string; onClick?: () => void }) {
  const row = (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={tip ? { cursor: 'help' } : undefined}>
      <Box sx={{ color: 'text.secondary', display: 'inline-flex', flexShrink: 0 }}>{icon}</Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 100 }}>{label}</Typography>
      <Typography onClick={onClick} sx={{
        fontSize: 13, fontWeight: 500,
        ...(onClick ? { color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}),
      }}>{value}</Typography>
    </Stack>
  )
  return tip ? <Tooltip title={tip} arrow placement="top-start"><Box>{row}</Box></Tooltip> : row
}
function FilterChip({ icon, label, active, value, onChange, options }: { icon: React.ReactNode; label: string; active: boolean; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} size="small" variant="standard" disableUnderline
      renderValue={() => <Stack direction="row" alignItems="center" spacing={0.85} sx={{ px: 0.5 }}><Box sx={{ color: active ? 'primary.main' : 'text.secondary', display: 'inline-flex' }}>{icon}</Box><Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: active ? 'text.primary' : 'text.secondary' }}>{label}</Box></Stack>}
      MenuProps={{ PaperProps: { sx: { minWidth: 200, borderRadius: 1.5, mt: 0.5 } } }}
      sx={{ height: 32, borderRadius: 2, border: '1px solid', borderColor: active ? 'primary.main' : 'divider', bgcolor: 'background.paper', '& .MuiSelect-select': { py: 0, pr: '28px !important', pl: 1.25 }, '& .MuiSelect-icon': { color: 'text.secondary', fontSize: 18, right: 6 }, '&:hover': { borderColor: active ? 'primary.main' : 'text.secondary' } }}>
      {options.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>)}
    </Select>
  )
}
